/**
 * Calculate BMI (Body Mass Index)
 * @param {number} weight - Weight in kg
 * @param {number} height - Height in cm
 * @returns {number} BMI value
 */
export const calculateBMI = (weight, height) => {
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

/**
 * Get BMI category
 * @param {number} bmi 
 * @returns {object} Category and color
 */
export const getBMICategory = (bmi) => {
  if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' };
  if (bmi < 25) return { category: 'Normal', color: 'text-green-600' };
  if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' };
  return { category: 'Obese', color: 'text-red-600' };
};

/**
 * Calculate estimated days to reach goal weight
 * @param {number} currentWeight 
 * @param {number} targetWeight 
 * @param {number} weeklyChange - Weekly weight change in kg (default 0.5kg/week)
 * @returns {number} Days to goal
 */
export const calculateDaysToGoal = (currentWeight, targetWeight, weeklyChange = 0.5) => {
  const totalChange = Math.abs(targetWeight - currentWeight);
  const weeks = totalChange / weeklyChange;
  return Math.round(weeks * 7);
};

/**
 * Calculate weekly weight reduction target
 * @param {string} goal - 'Lose', 'Maintain', or 'Gain'
 * @returns {number} Weekly target change in kg
 */
export const getWeeklyWeightTarget = (goal) => {
  if (goal === 'Lose') {
    return -0.5; // Healthy weight loss: 0.5 kg per week
  } else if (goal === 'Gain') {
    return 0.3; // Healthy weight gain: 0.3 kg per week
  }
  return 0; // Maintain
};

/**
 * Get the start of the current week (Monday)
 * @param {Date} date 
 * @returns {Date} Start of week
 */
export const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

/**
 * Get week number of the year
 * @param {Date} date 
 * @returns {number} Week number
 */
export const getWeekNumber = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * Calculate weekly weight change from weight entries
 * @param {Array} weightEntries - Array of weight entries sorted by date (newest first)
 * @returns {Object} { weeklyChange, startWeight, endWeight, startDate, endDate }
 */
export const calculateWeeklyChange = (weightEntries) => {
  if (weightEntries.length < 2) {
    return { weeklyChange: 0, startWeight: null, endWeight: null };
  }

  const weekStart = getWeekStart();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  // Find weights at start and end of week
  const endWeight = weightEntries[0]; // Most recent
  const startWeight = weightEntries.find(entry => {
    const entryDate = new Date(entry.date);
    return entryDate <= weekStart;
  });

  if (!startWeight) {
    return { weeklyChange: 0, startWeight: null, endWeight: endWeight.weight };
  }

  const change = endWeight.weight - startWeight.weight;
  
  return {
    weeklyChange: change,
    startWeight: startWeight.weight,
    endWeight: endWeight.weight,
    startDate: startWeight.date,
    endDate: endWeight.date
  };
};

/**
 * Check if weekly target is achieved
 * @param {number} actualChange - Actual weight change this week
 * @param {number} targetChange - Target weight change per week
 * @param {number} tolerance - Tolerance in kg (default 0.2)
 * @returns {Object} { achieved, status, message }
 */
export const checkWeeklyTarget = (actualChange, targetChange, tolerance = 0.2) => {
  const diff = Math.abs(actualChange - targetChange);
  
  if (targetChange < 0) {
    // Weight loss goal
    if (actualChange <= targetChange + tolerance && actualChange <= 0) {
      return { achieved: true, status: 'success', message: 'On track! Great progress!' };
    } else if (actualChange > 0) {
      return { achieved: false, status: 'warning', message: 'Weight increased. Stay focused!' };
    } else if (actualChange < targetChange - tolerance) {
      return { achieved: false, status: 'caution', message: 'Losing too fast. Slow down a bit.' };
    } else {
      return { achieved: false, status: 'behind', message: 'Not enough progress. Keep going!' };
    }
  } else if (targetChange > 0) {
    // Weight gain goal
    if (actualChange >= targetChange - tolerance && actualChange >= 0) {
      return { achieved: true, status: 'success', message: 'On track! Keep it up!' };
    } else {
      return { achieved: false, status: 'behind', message: 'Need more progress.' };
    }
  } else {
    // Maintain weight
    if (Math.abs(actualChange) <= tolerance) {
      return { achieved: true, status: 'success', message: 'Maintaining well!' };
    } else {
      return { achieved: false, status: 'warning', message: 'Weight fluctuating.' };
    }
  }
};

/**
 * Calculate total calories from macros
 * @param {number} protein 
 * @param {number} carbs 
 * @param {number} fat 
 * @returns {number} Total calories
 */
export const calculateCaloriesFromMacros = (protein, carbs, fat) => {
  return (protein * 4) + (carbs * 4) + (fat * 9);
};

/**
 * Convert feet and inches to centimeters
 * @param {number} feet 
 * @param {number} inches 
 * @returns {number} Height in cm
 */
export const feetInchesToCm = (feet, inches) => {
  const totalInches = (feet * 12) + inches;
  return totalInches * 2.54;
};

/**
 * Convert centimeters to feet and inches
 * @param {number} cm 
 * @returns {object} { feet, inches }
 */
export const cmToFeetInches = (cm) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
};

/**
 * Calculate BMR (Basal Metabolic Rate) using Mifflin-St Jeor Equation
 * @param {number} weight - Weight in kg
 * @param {number} height - Height in cm
 * @param {number} age - Age in years
 * @param {string} gender - 'M' or 'F'
 * @returns {number} BMR in calories/day
 */
export const calculateBMR = (weight, height, age, gender) => {
  // Mifflin-St Jeor Equation
  // Men: BMR = 10W + 6.25H - 5A + 5
  // Women: BMR = 10W + 6.25H - 5A - 161
  const baseCalc = (10 * weight) + (6.25 * height) - (5 * age);
  
  if (gender === 'M') {
    return baseCalc + 5;
  } else {
    return baseCalc - 161;
  }
};

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 * @param {number} bmr - Basal Metabolic Rate
 * @param {string} activityLevel - 'Sedentary', 'Light', 'Moderate', 'Active', 'VeryActive'
 * @returns {number} TDEE in calories/day
 */
export const calculateTDEE = (bmr, activityLevel) => {
  const activityMultipliers = {
    'Sedentary': 1.2,      // Little or no exercise
    'Light': 1.375,        // Light exercise 1-3 days/week
    'Moderate': 1.55,      // Moderate exercise 3-5 days/week
    'Active': 1.725,       // Hard exercise 6-7 days/week
    'VeryActive': 1.9      // Very hard exercise & physical job
  };
  
  return Math.round(bmr * (activityMultipliers[activityLevel] || 1.55));
};

/**
 * Calculate daily calorie target based on weight goal
 * @param {number} tdee - Total Daily Energy Expenditure
 * @param {string} goal - 'Lose', 'Maintain', 'Gain'
 * @returns {number} Target calories/day
 */
export const calculateCalorieTarget = (tdee, goal) => {
  if (goal === 'Lose') {
    // Deficit of 500 calories/day for ~0.5kg loss per week
    return Math.round(tdee - 500);
  } else if (goal === 'Gain') {
    // Surplus of 300-500 calories/day for lean muscle gain
    return Math.round(tdee + 400);
  } else {
    // Maintain weight
    return tdee;
  }
};

/**
 * Format date for display
 * @param {Date|string} date 
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

/**
 * Format time for display
 * @param {Date|string} date 
 * @returns {string} Formatted time
 */
export const formatTime = (date) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * Get progress percentage
 * @param {number} current 
 * @param {number} goal 
 * @returns {number} Percentage (capped at 100)
 */
export const getProgressPercentage = (current, goal) => {
  if (!goal) return 0;
  return Math.min(Math.round((current / goal) * 100), 100);
};

/**
 * Get progress color based on percentage
 * @param {number} percentage 
 * @returns {string} Tailwind color class
 */
export const getProgressColor = (percentage) => {
  if (percentage >= 100) return 'text-green-500';
  if (percentage >= 75) return 'text-blue-500';
  if (percentage >= 50) return 'text-yellow-500';
  return 'text-gray-400';
};

/**
 * Calculate streak from activity history
 * @param {Array} history - Array of date entries
 * @returns {number} Current streak
 */
export const calculateStreak = (history) => {
  if (!history || history.length === 0) return 0;
  
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const sortedHistory = [...history].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );
  
  for (let i = 0; i < sortedHistory.length; i++) {
    const entryDate = new Date(sortedHistory[i].date);
    entryDate.setHours(0, 0, 0, 0);
    
    const expectedDate = new Date(today);
    expectedDate.setDate(today.getDate() - i);
    
    if (entryDate.getTime() === expectedDate.getTime()) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

/**
 * Get motivational message based on progress
 * @param {number} percentage 
 * @returns {string} Motivational message
 */
export const getMotivationalMessage = (percentage) => {
  if (percentage >= 100) return "🎉 Amazing! Goal achieved!";
  if (percentage >= 75) return "🔥 You're almost there!";
  if (percentage >= 50) return "💪 Keep pushing!";
  if (percentage >= 25) return "🌟 Great start!";
  return "🚀 Let's get started!";
};

/**
 * Aggregate data by time period
 * @param {Array} data 
 * @param {string} period - 'daily', 'weekly', 'monthly'
 * @returns {Array} Aggregated data
 */
export const aggregateData = (data, period = 'daily') => {
  // Implementation for aggregating data by period
  // For now, return daily data
  return data;
};
